import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as attendanceService from "../service/attendanceService";
import { errorMessage } from "@/lib/notify";
import type { AttendanceState, MarkAttendancePayload } from "../types";
import i18n from "@/i18n";

export const fetchRoster = createAsyncThunk(
  "attendance/fetchRoster",
  async ({ institutionId, academicGroupId, academicYearId, date }: { institutionId: number; academicGroupId: number; academicYearId: number; date: string }, { rejectWithValue }) => {
    try {
      return await attendanceService.fetchRoster(institutionId, { academicGroupId, academicYearId, date });
    } catch (err) {
      return rejectWithValue(errorMessage(err, i18n.t("attendance:errors.failedToLoadRoster")));
    }
  }
);

export const markAttendance = createAsyncThunk(
  "attendance/markAttendance",
  async ({ institutionId, payload }: { institutionId: number; payload: MarkAttendancePayload }, { rejectWithValue }) => {
    try {
      return await attendanceService.markAttendance(institutionId, payload);
    } catch (err) {
      return rejectWithValue(errorMessage(err, i18n.t("attendance:errors.failedToSaveAttendance")));
    }
  }
);

export const fetchSummary = createAsyncThunk(
  "attendance/fetchSummary",
  async ({ institutionId, academicGroupId, from, to }: { institutionId: number; academicGroupId?: number; from?: string; to?: string }) => {
    return await attendanceService.fetchSummary(institutionId, { academicGroupId, from, to });
  }
);

const initialState: AttendanceState = {
  roster: [],
  marked: false,
  dateIssue: null,
  currentRequestId: null,
  summary: [],
  loading: false,
  saving: false,
  error: null,
};

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoster.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchRoster.fulfilled, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.roster = action.payload.roster;
        state.marked = action.payload.marked;
        state.dateIssue = action.payload.dateIssue;
      })
      .addCase(fetchRoster.rejected, (state, action) => {
        if (state.currentRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.roster = [];
        state.marked = false;
        state.dateIssue = null;
        state.error = (action.payload as string) ?? i18n.t("attendance:errors.failedToLoadRoster");
      })
      .addCase(markAttendance.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(markAttendance.fulfilled, (state, action) => {
        state.saving = false;
        state.roster = action.payload.roster;
        state.marked = action.payload.marked;
        state.dateIssue = action.payload.dateIssue;
      })
      .addCase(markAttendance.rejected, (state, action) => {
        state.saving = false;
        state.error = (action.payload as string) ?? i18n.t("attendance:errors.failedToSaveAttendance");
      })
      .addCase(fetchSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      });
  },
});

export default attendanceSlice.reducer;
